import * as React from "react";
import { clsx } from "clsx";
import { ToggleReact } from "common/components/toggle";
import { ToggleTheme } from "common/components/toggle/types";
import { ButtonSize, ButtonTheme, ButtonType } from "common/components/button/constants";
import { ButtonReact } from "common/components/button";
import { SelectReact } from "common/components/select";
import { SlackChannelSelectorReact } from "common/widgets/slack-channel-selector";
import { InputReact } from "common/components/input";
import { UserIconReact } from "common/widgets/user-icon";
import { DropMenuReact } from "common/components/drop-menu";
import { ItemsMenuReact } from "common/components/items-menu";
import { SpinnerReact } from "common/components/spinner";
import { SIZE, TextHighlightReact } from "common/components/text-highlight";
import { SelectTheme } from "common/components/select/constants";
import { getFormattedUserName } from "common/widgets/sharing/modal";
import { addExternalUserFn } from "common/query-builder/query-report-header/subscribe-to-boards-menu/util";
import { IconSizeType } from "common/constants";
import sharingModalStyles from "common/widgets/sharing/modal/index.module.styl";
import styles from "./index.module.styl";
import {
  DIGEST_RECURRENCE_FRIENDLY_NAME,
  DigestDateOrdinal,
  DigestRecurrence,
  DigestType,
  LOADING_STATES,
  SUBSCRIBE_WEEK_DAYS,
  SubscribeSendMethod
} from "./types";
import { DIGEST_COPY } from "./constants";
const FrequencySelector = (props) => {
  const { recurrence, selectedTimezone, monthlyDateOrdinal, startDate } = props.cmp.state;
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.buttonContainer)
  }, [DigestRecurrence.Daily, DigestRecurrence.Weekly, DigestRecurrence.Monthly].map((option) => /* @__PURE__ */ React.createElement(ButtonReact, {
    className: clsx(styles.button, { [styles.selected]: recurrence === option }),
    key: option,
    theme: ButtonTheme.Tertiary,
    onClick: () => props.cmp.onSelectFrequency(option)
  }, DIGEST_RECURRENCE_FRIENDLY_NAME[option]))), /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.frequencySelectContainer, {
      [styles.dailyLayout]: recurrence === DigestRecurrence.Daily,
      [styles.monthlyLayout]: recurrence === DigestRecurrence.Monthly,
      [styles.weeklyLayout]: recurrence === DigestRecurrence.Weekly
    })
  }, recurrence === DigestRecurrence.Monthly && /* @__PURE__ */ React.createElement(SelectReact, {
    key: "ordinal",
    selectedOption: props.cmp.getMonthlyOrdinalName(monthlyDateOrdinal),
    onChange: ({ detail }) => props.cmp.updateMonthlyDateOrdinal(detail.item.value),
    sections: [
      {
        items: [DigestDateOrdinal.First, DigestDateOrdinal.Last].map((ordinal) => ({
          label: props.cmp.getMonthlyOrdinalName(ordinal),
          value: ordinal
        }))
      }
    ]
  }), [DigestRecurrence.Weekly, DigestRecurrence.Monthly].includes(recurrence) && /* @__PURE__ */ React.createElement(SelectReact, {
    key: "day",
    selectedOption: startDate.format(`dddd`),
    onChange: ({ detail }) => props.cmp.updateSelectedWeeklyDay(detail.item.value),
    sections: [{ items: SUBSCRIBE_WEEK_DAYS.map((label) => ({ label, value: label })) }]
  }), /* @__PURE__ */ React.createElement(SelectReact, {
    key: "hour",
    selectedOption: startDate.format(`h A`),
    onChange: ({ detail }) => props.cmp.updateHourOfDay(detail.item.value),
    sections: [
      {
        items: props.cmp.hoursOfTheDay.map((label, index) => ({ label, value: index }))
      }
    ]
  }), /* @__PURE__ */ React.createElement(SelectReact, {
    className: clsx(styles.timezoneSelect),
    key: "timezone",
    menuWidth: 280,
    selectedOption: selectedTimezone,
    onChange: ({ detail }) => props.cmp.updateTimezone(detail.item.value),
    sections: [{ items: props.cmp.timezones.map((label) => ({ label, value: label })) }]
  })));
};
const SubscribeButton = (props) => {
  const isSaving = props.cmp.state.isSavingDigest;
  let disabled = props.disabled;
  if (isSaving) {
    disabled = true;
  }
  return /* @__PURE__ */ React.createElement(ButtonReact, {
    className: clsx(styles.subscribeButton),
    disabled,
    styleOverride: `a.mp-button-container .mp-button-content {
            flex: none; width: auto;
          }`,
    theme: ButtonTheme.Primary,
    onClick: () => props.cmp.createDigest(),
    ...isSaving ? { icon: `spinner` } : {}
  }, "Subscribe");
};
const UpdateView = (props) => {
  const { isDeletingDigest, isSavingDigest } = props.cmp.state;
  return /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.digestListActions)
  }, /* @__PURE__ */ React.createElement(ButtonReact, {
    className: clsx(styles.button),
    disabled: props.cmp.editButtonsDisabled,
    onClick: () => props.cmp.onDeleteDigest(props.cmp.state.currentDigest.id),
    type: ButtonType.Destructive,
    ...isDeletingDigest ? { icon: `spinner` } : {}
  }, "Delete"), /* @__PURE__ */ React.createElement(ButtonReact, {
    className: clsx(styles.button),
    disabled: props.cmp.editButtonsDisabled,
    onClick: () => props.cmp.patchDigestFromEditScreen(props.cmp.state.currentDigest.id),
    theme: ButtonTheme.Primary,
    ...isSavingDigest ? { icon: `spinner` } : {}
  }, "Update"));
};
const SubscribeToExistingDigestButton = (props) => {
  const currentUserSubbed = props.cmp.currentUserIsAlreadySubscribedToDigest(props.digest.id);
  const updatingCurrentDigest = props.cmp.state.digestIsBeingUpdatedMap.get(props.digest.id);
  let icon = `feed`;
  if (updatingCurrentDigest) {
    icon = `spinner`;
  } else if (currentUserSubbed) {
    icon = `checkmark`;
  }
  return /* @__PURE__ */ React.createElement(ButtonReact, {
    icon,
    size: ButtonSize.Small,
    theme: ButtonTheme.Secondary,
    onClick: (event) => {
      event.stopPropagation();
      props.cmp.onSubscribeToExistingDigest(props.digest, currentUserSubbed);
    }
  }, currentUserSubbed ? `Subscribed` : `Subscribe`);
};
const DashboardSelector = (props) => {
  var _a;
  if (props.cmp.props.digestToUpdate) {
    return null;
  }
  return /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.dashboardSelectContainer)
  }, /* @__PURE__ */ React.createElement(SelectReact, {
    className: clsx(styles.dashboardSelect),
    icon: { leftIcon: `icon-grid` },
    sections: [props.cmp.getDashboardSections()],
    selectedOption: ((_a = props.cmp.state.selectedDashboard) == null ? void 0 : _a.title) || `Select Board`,
    theme: SelectTheme.Secondary,
    onChange: ({ detail }) => props.cmp.onClickDashboard(detail.item.value)
  })));
};
const DetailView = (props) => {
  const { currentTab, isSendingTestDigest } = props.cmp.state;
  return /* @__PURE__ */ React.createElement("div", null, !props.cmp.props.dashboardId ? /* @__PURE__ */ React.createElement(DashboardSelector, {
    cmp: props.cmp
  }) : null, /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.buttonContainer)
  }, [SubscribeSendMethod.Email, SubscribeSendMethod.Slack].map((option) => /* @__PURE__ */ React.createElement(ButtonReact, {
    className: clsx(styles.button, { [styles.selected]: currentTab === option }),
    key: option,
    theme: ButtonTheme.Tertiary,
    onClick: () => props.cmp.updateCurrentTab(option)
  }, option))), /* @__PURE__ */ React.createElement(FrequencySelector, {
    cmp: props.cmp
  }), /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.subscriberSection)
  }, /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.subscriberTitle)
  }, /* @__PURE__ */ React.createElement("span", {
    className: clsx(styles.subscriberCount)
  }, "Subscribers (", props.subscribersCount, ")"), /* @__PURE__ */ React.createElement(ButtonReact, {
    disabled: props.disableSendOrSubscribe || props.cmp.editButtonsDisabled,
    theme: ButtonTheme.Tertiary,
    onClick: () => props.cmp.onSendTest(),
    ...isSendingTestDigest ? { icon: `spinner` } : {}
  }, "Send Test")), props.children), props.cmp.state.currentDigest ? /* @__PURE__ */ React.createElement(UpdateView, {
    cmp: props.cmp
  }) : /* @__PURE__ */ React.createElement(SubscribeButton, {
    cmp: props.cmp,
    disabled: props.disableSendOrSubscribe
  }));
};
const SlackView = (props) => {
  const { isEditDisabled, slackSubscriptions } = props.cmp.state;
  const disableSendOrSubscribe = !slackSubscriptions.length || !props.cmp.props.dashboardId && !props.cmp.state.selectedDashboard;
  return /* @__PURE__ */ React.createElement(DetailView, {
    cmp: props.cmp,
    disableSendOrSubscribe,
    subscribersCount: slackSubscriptions.length
  }, /* @__PURE__ */ React.createElement(SlackChannelSelectorReact, {
    buttonFullWidth: true,
    buttonTheme: ButtonTheme.Primary,
    className: clsx(styles.connectSlackButton),
    isReadonly: isEditDisabled,
    selectedChannels: slackSubscriptions.map((subscription) => {
      return { name: subscription.slack_channel_name, id: subscription.slack_channel_id };
    }),
    onSelectedChannelsChanged: (event) => props.cmp.onSlackSubscriptionsChanged(event)
  }));
};
export const EmailRecipients = (props) => {
  return /* @__PURE__ */ React.createElement("div", {
    className: clsx(sharingModalStyles.shareSectionWrapper)
  }, /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.shareSection, sharingModalStyles.shareSection)
  }, /* @__PURE__ */ React.createElement("ul", {
    className: clsx(sharingModalStyles.shareList),
    role: `list`
  }, props.emailSubscribers.map((pm) => props.membersMap.get(pm.email) || pm).map((member, index) => {
    const { id, name, email } = member;
    return /* @__PURE__ */ React.createElement("li", {
      className: clsx(sharingModalStyles.shareListItem),
      key: email,
      role: `listitem`
    }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(UserIconReact, {
      className: clsx(sharingModalStyles.mpUserIcon),
      disableTooltip: true,
      email,
      id,
      size: IconSizeType.Small,
      ...{ name: name || email }
    }))), /* @__PURE__ */ React.createElement("div", {
      className: clsx(sharingModalStyles.display)
    }, /* @__PURE__ */ React.createElement("div", {
      className: clsx(sharingModalStyles.nameWrapper)
    }, /* @__PURE__ */ React.createElement("div", {
      className: clsx(sharingModalStyles.name)
    }, getFormattedUserName(name, email))), /* @__PURE__ */ React.createElement("div", {
      className: clsx(sharingModalStyles.subtitle)
    }, email)), /* @__PURE__ */ React.createElement("div", {
      className: clsx(sharingModalStyles.controls)
    }, /* @__PURE__ */ React.createElement(ButtonReact, {
      className: clsx(styles.removeButton),
      icon: `trashcan`,
      theme: ButtonTheme.Tertiary,
      onClick: () => props.removeFunc(index)
    })));
  }))));
};
const EmailView = (props) => {
  var _a;
  const { emailSubscribers, subscriberSearch } = props.cmp.state;
  const disableSendOrSubscribe = !emailSubscribers.length || Boolean(props.cmp.updateSubscriberSearchError) || !props.cmp.props.dashboardId && !props.cmp.state.selectedDashboard;
  return /* @__PURE__ */ React.createElement(DetailView, {
    cmp: props.cmp,
    disableSendOrSubscribe,
    subscribersCount: emailSubscribers.length
  }, /* @__PURE__ */ React.createElement("div", null, /* @__PURE__ */ React.createElement(InputReact, {
    errorMessage: props.cmp.updateSubscriberSearchError,
    ref: props.cmp.searchInputElementRef,
    placeholder: `Search by name or email`,
    validationStatus: props.cmp.updateSubscriberSearchError ? `invalid` : ``,
    onFocus: () => props.cmp.onFocus(),
    onBlur: () => props.cmp.onBlur(),
    onInput: ({ target }) => props.cmp.updateSubscriberSearch(target.value),
    onKeydown: (ev) => props.cmp.onSubscriberSearchKeydown(ev),
    value: subscriberSearch
  }), /* @__PURE__ */ React.createElement(DropMenuReact, {
    open: !!subscriberSearch && props.cmp.state.subscriberSearchFocused
  }, /* @__PURE__ */ React.createElement(ItemsMenuReact, {
    className: clsx(styles.memberDropdown),
    emptySearchResultsMessage: DIGEST_COPY.emailNoResults,
    keydownProxyEl: (_a = props.cmp.searchInputElementRef) == null ? void 0 : _a.current,
    searchFilter: subscriberSearch,
    sections: [props.cmp.getSections()],
    sectionsFromInputFn: (input) => addExternalUserFn(input, props.cmp.state.emailDomains),
    onClickItem: (ev) => props.cmp.onClickMember(ev.detail.item.value),
    onFocusItem: () => props.cmp.onFocus()
  }))), emailSubscribers.length ? /* @__PURE__ */ React.createElement(EmailRecipients, {
    emailSubscribers,
    membersMap: props.cmp.state.membersMap,
    removeFunc: (index) => props.cmp.removeSubscriber(index)
  }) : /* @__PURE__ */ React.createElement("div", null));
};
const LoadingMessage = () => /* @__PURE__ */ React.createElement("div", {
  className: clsx(styles.loadingMessage)
}, /* @__PURE__ */ React.createElement(SpinnerReact, null), /* @__PURE__ */ React.createElement("span", null, "Loading data..."));
const DigestItemSubscriberSummary = (props) => {
  const isEmail = props.digestType === DigestType.Email;
  if (isEmail) {
    const recipientCount = props.digest.recipients.length;
    const pluralSuffix = recipientCount > 1 ? `s` : ``;
    const recipientString = `${recipientCount} subscriber${pluralSuffix}`;
    return /* @__PURE__ */ React.createElement("span", null, recipientString);
  } else {
    return /* @__PURE__ */ React.createElement("span", null, props.digest.slack_subscriptions.map((slackChannel) => {
      return /* @__PURE__ */ React.createElement("span", {
        key: slackChannel.slack_channel_id
      }, "#", slackChannel.slack_channel_name, "\xA0");
    }));
  }
};
export const ListView = (props) => {
  const { availableMembers, currentTab, dashboardDigests } = props.cmp.state;
  const digestType = currentTab === SubscribeSendMethod.Slack ? DigestType.Slack : DigestType.Email;
  const digestsList = digestType === DigestType.Email ? props.cmp.emailDigests : props.cmp.slackDigests;
  const hasDigests = Boolean(digestsList.length);
  const noDigestsMessage = currentTab === SubscribeSendMethod.Slack ? `No Slack subscriptions found for this board.` : `No email subscriptions found for this board.`;
  const dataIsLoading = !props.cmp.currentUserEmail || LOADING_STATES.includes(availableMembers.loading) || !props.cmp.props.createOnly && LOADING_STATES.includes(dashboardDigests.loading);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.subscribeToBoardsNav, styles.listView)
  }, /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.subscribeToBoardsToggleContainer)
  }, /* @__PURE__ */ React.createElement(ToggleReact, {
    className: clsx(styles.subscribeToBoardsToggle),
    options: [
      {
        icon: `email`,
        label: `${SubscribeSendMethod.Email} ${props.cmp.emailDigestsCountString}`,
        value: SubscribeSendMethod.Email
      },
      {
        icon: `slack-logo`,
        label: `${SubscribeSendMethod.Slack} ${props.cmp.slackDigestsCountString}`,
        value: SubscribeSendMethod.Slack
      }
    ],
    selected: currentTab,
    theme: ToggleTheme.TabBar,
    onSelect: ({ detail }) => props.cmp.updateCurrentTab(detail.selected)
  }))), /* @__PURE__ */ React.createElement("div", null, dataIsLoading ? /* @__PURE__ */ React.createElement(LoadingMessage, null) : /* @__PURE__ */ React.createElement("div", {
    className: clsx({ [styles.hasEntries]: hasDigests })
  }, !hasDigests ? /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.noDigestsMessage)
  }, noDigestsMessage) : /* @__PURE__ */ React.createElement("div", null), /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.digestList)
  }, /* @__PURE__ */ React.createElement("div", {
    role: `list`
  }, digestsList.map((digest) => {
    return /* @__PURE__ */ React.createElement("div", {
      className: clsx(styles.digestEntry, { [styles.clickable]: props.cmp.canEditDigest(digest) }),
      key: digest.id,
      onClick: () => props.cmp.canEditDigest(digest) ? props.cmp.onSelectDigest(digest) : {},
      role: `listitem`
    }, /* @__PURE__ */ React.createElement("div", {
      className: clsx(styles.digestHeader)
    }, /* @__PURE__ */ React.createElement("span", null, digest.schedule), digestType === DigestType.Email ? /* @__PURE__ */ React.createElement(SubscribeToExistingDigestButton, {
      cmp: props.cmp,
      digest
    }) : /* @__PURE__ */ React.createElement("span", null)), /* @__PURE__ */ React.createElement("div", {
      className: clsx(styles.digestBody)
    }, digest.timezone), /* @__PURE__ */ React.createElement("div", {
      className: clsx(styles.digestFooter)
    }, props.cmp.currentUserIsOwnerOfDigest(digest) ? /* @__PURE__ */ React.createElement(TextHighlightReact, {
      className: clsx(styles.textHighlight),
      size: SIZE.SMALL,
      text: `Owner`
    }) : /* @__PURE__ */ React.createElement("span", null), /* @__PURE__ */ React.createElement(DigestItemSubscriberSummary, {
      digest,
      digestType
    })));
  }))), /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.digestListActions)
  }, /* @__PURE__ */ React.createElement(ButtonReact, {
    className: clsx(styles.button),
    href: props.cmp.manageDigestsModeUrl,
    icon: `externalLink`,
    theme: ButtonTheme.Secondary
  }, "View All"), /* @__PURE__ */ React.createElement(ButtonReact, {
    className: clsx(styles.button),
    theme: ButtonTheme.Primary,
    onClick: () => props.cmp.onCreateNewDigest()
  }, "Create New")))));
};
export const CreateOrEditView = (props) => {
  const { currentDigest, currentTab } = props.cmp.state;
  const editing = Boolean(currentDigest);
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.subscribeToBoardsNav)
  }, /* @__PURE__ */ React.createElement(ButtonReact, {
    className: clsx(styles.subscribeToBoardsNavBackButton),
    icon: `arrow-left`,
    theme: ButtonTheme.Tertiary,
    onClick: () => props.cmp.onNavBackButton()
  }), /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.subscribeToBoardsNavTitle)
  }, editing ? `Edit a Subscription` : `Create a Subscription`)), /* @__PURE__ */ React.createElement("div", {
    className: clsx(styles.subscribeToBoardsContent)
  }, currentTab === SubscribeSendMethod.Slack && /* @__PURE__ */ React.createElement(SlackView, {
    cmp: props.cmp
  }), currentTab === SubscribeSendMethod.Email && /* @__PURE__ */ React.createElement(EmailView, {
    cmp: props.cmp
  })));
};
